import {
  selectCmsItemsForLocales,
  selectCmsItemsIgnoringLocalization,
} from '@packages/systems/cms/client-selectors';
import {fromJS, Map, List, Iterable} from 'immutable';
import moment from 'moment-timezone';
import {
  isMint,
  makeMintId,
  indexById,
  isDraft,
  getId,
  getSlug,
} from '@packages/systems/core/utils/RecordUtils';
import {getElse, leftJoin, leftJoinWith} from '@packages/utilities/data-utils';
import {map, either, notNil, isNil} from '@packages/utilities/fp/utils';
import {reverse, head} from '@packages/systems/core/utils/ImmUtils';
import {
  isItemRefSet,
  isSingleRef,
  isItemRef,
  isOption,
} from '@packages/systems/dynamo/utils/CollectionFieldUtils';
import {curry, flowRight, identity, toLower} from 'lodash';
import {getHue} from '@packages/systems/core/utils/ColorUtils';
import type {ItemRecordType as Item} from '@packages/systems/core/records/ItemRecord';
import {ImageRefRecord} from '@packages/systems/core/records/ImageRefRecord';
import {
  videoRecordToMap,
  reify as reifyVideo,
} from '@packages/systems/core/records/VideoRecord';
import type {CollectionRecordType as Collection} from '@packages/systems/dynamo/records/CollectionRecord';
import {
  type CollectionStateType as CollectionState,
  type CollectionsType,
  ItemId,
  type ItemsType,
  mapCmsItemsById,
  type ScheduleItem,
  type ScheduleItemsType,
} from '@packages/systems/core/records/CollectionStateType';
import type {FieldType as Field} from '@packages/systems/dynamo/records/Field';
import {cacheMemo, weakMemo} from '@packages/utilities/memo';
import {
  CMS_CHANGES_IN_DRAFT_LABEL,
  CMS_ITEM_STATUS,
  CmsItemStatusValues,
} from '@packages/systems/cms/constants';
import {CurrentCollectionState} from '@packages/systems/cms/CurrentCollectionStore';
import {type Locale} from '@packages/systems/localization-types';
import {type TextProps, type ButtonProps} from '@packages/systems/spring';

export function mintItem(localeId?: string | null): Item {
  let item = Map({
    _id: makeMintId(),
    _draft: false,
    _archived: false,
    name: '',
    slug: '',
  });

  if (localeId) {
    item = item.set('_locale', localeId);
  }

  return item;
}

export function reifyItem(collection: Collection, raw: Record<any, any>): any {
  return Map(raw).map((v, k) => {
    const field = collection.fields.find((f) => f.get('slug') === k);
    return field ? reifyFieldValue(field, v) : v;
  });
}

function reifyFieldValue(field: any, value: any) {
  if (value == null) {
    return value;
  }

  switch (field.get('type')) {
    case 'ImageRef':
      return ImageRefRecord.reify(value);
    case 'Video':
      return reifyVideo(videoRecordToMap(value));
    default:
      return fromJS(value);
  }
}

export const sortItems = curry(
  (
    ascending: boolean,
    fieldType: string,
    fieldSlug: string,
    itemList: List<Item>
  ) => {
    const sorted = itemList.sort(
      byAscendingWithCreatedOnFallback(ascending, fieldType, fieldSlug)
    );
    return ascending ? sorted : reverse(sorted);
  }
);

// asComparator :: Comparable c => (a -> c) -> (a, a) -> Number
const asComparator = (fn: any) => (x: any, y: any) => {
  x = fn(x);
  y = fn(y);

  if (x < y) {
    return -1;
  } else if (x > y) {
    return 1;
  } else {
    return 0;
  }
};

// Does the same thing as byAscending, but falls back to created-on
const byAscendingWithCreatedOnFallback = (
  ascending: boolean,
  fieldType: string,
  fieldSlug: string
) =>
  either(
    asComparator(byAscending(ascending, fieldType, fieldSlug)),
    /* ⚡️ PERF WARNING ⚠️
      Date fields are stored as UTC Date Strings (e.g. "2020-04-16T22:29:36.326Z"),
      so instead of using the slow Date.parse method, we can directly compare these
      strings in the sort algorithm with the same result
    */
    asComparator(getElse('created-on', '0'))
  );

const defaultTo = (fallback: unknown) => (value: unknown) =>
  value == null || value !== value // eslint-disable-line no-self-compare
    ? fallback
    : value;

// byAscending :: Comparable c => (Boolean, fieldType, fieldSlug) -> Item -> c
const byAscending = (
  ascending: boolean,
  fieldType: string,
  fieldSlug: string
) => {
  const getValueOr = getElse(fieldSlug);

  switch (fieldType) {
    case 'Date':
      /* ⚡️ PERF WARNING ⚠️
        Date fields are stored as UTC Date Strings (e.g. "2020-04-16T22:29:36.326Z"),
        so instead of using the slow Date.parse method, we can directly compare these
        strings in the sort algorithm with the same result
      */
      return getValueOr('0');
    case 'Bool':
      return flowRight(getValueOr(false));
    case 'Color':
      return flowRight(defaultTo(-1), getHue, getValueOr(null));
    case 'Number':
      return getValueOr(0);
    case 'CommercePrice':
      return flowRight(getElse('value', 0), getValueOr(Map())); // CommercePrice has format Map({value: 5, unit: 'USD'})
    case 'Status':
      return flowRight(statusSort(ascending), getValueOr('new'));
    default:
      return flowRight(toLower, getValueOr(''));
  }
};

const statusSort = (ascending: boolean) => (status: any) =>
  ascending
    ? statusDraftFirst.indexOf(status)
    : -statusPublishedFirst.indexOf(status);

const statusDraftFirst = [
  'new',
  'draft',
  'staged',
  'published',
  'stagedChanges',
  'archived',
];

const statusPublishedFirst = [
  'new',
  'stagedChanges',
  'published',
  'staged',
  'draft',
  'archived',
];

export function getStatus({
  item,
  scheduledItems = [],
  lastPublished,
}: {
  item: Item;
  scheduledItems?: ScheduleItemsType;
  lastPublished?: string | null;
}): string {
  const itemId = item.get('_id', '');
  const localeId = item.get('_locale');

  if (isMint(item)) {
    return 'new';
  } else if (isArchived(item)) {
    return 'archived';
  } else if (isSuccessfullyScheduledItem({itemId, localeId, scheduledItems})) {
    return 'scheduled';
  } else if (isFailedScheduledItem({itemId, localeId, scheduledItems})) {
    return 'scheduledFailed';
  } else if (isDraft(item)) {
    return 'draft';
  } else if (isPublished(item, lastPublished)) {
    /* ⚡️ PERF WARNING ⚠️
      Only check isPublishedWithChanges if the the item has been published,
      avoiding unnecessary computations
     */
    if (isPublishedWithChanges(item)) {
      return 'stagedChanges';
    }
    return 'published';
  } else {
    return 'staged';
  }
}

const matchesScheduledItem = (
  scheduledItem: ScheduleItem,
  itemId: string,
  localeId?: string
) => {
  if (localeId) {
    return (
      scheduledItem.itemId === itemId && scheduledItem.localeId === localeId
    );
  }
  return scheduledItem.itemId === itemId;
};

const getScheduledStatus = ({
  itemId,
  localeId,
  scheduledItems = [],
  timezone,
  statusFormat = 'default',
}: {
  itemId: string;
  localeId?: string;
  scheduledItems: ScheduleItemsType;
  timezone: string;
  statusFormat?: string;
}) => {
  const schedule = scheduledItems.find((i) =>
    matchesScheduledItem(i, itemId, localeId)
  );
  if (!schedule) {
    return '';
  }
  const date = moment.tz(schedule.scheduledTime, timezone);
  const defaultStatus = `Scheduled for ${date.format(
    'MMM DD, YYYY'
  )}, ${date.format('h:mm A')}`;
  const responses = {
    default: defaultStatus,
    forList: `Scheduled ${date.format('M/D/YY')}, ${date.format('h:mm A')}`,
    forAnalytics: 'Scheduled',
  } as const;

  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly default: string; readonly forList: `Scheduled ${string}, ${string}`; readonly forAnalytics: "Scheduled"; }'.
  return responses[statusFormat];
};

const getFailedToPublishStatus = ({
  itemId,
  localeId,
  scheduledItems = [],
  statusFormat = 'default',
}: {
  itemId: string;
  localeId?: string;
  scheduledItems: ScheduleItemsType;
  statusFormat?: string;
}) => {
  const scheduledItem = scheduledItems.find((i) =>
    matchesScheduledItem(i, itemId, localeId)
  );
  if (!scheduledItem) {
    return '';
  }
  const defaultStatus = 'Failed to publish';
  const responses = {
    default: defaultStatus,
    forList: defaultStatus,
    forTooltip: scheduledItem.publishError,
    forAnalytics: defaultStatus,
  } as const;

  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly default: "Failed to publish"; readonly forList: "Failed to publish"; readonly forTooltip: string; readonly forAnalytics: "Failed to publish"; }'.
  return responses[statusFormat];
};

export function getDisplayStatus(
  item: Item,
  lastPublished: string | null,
  {
    scheduledItems = [],
    timezone = '',
    statusFormat,
  }: {
    scheduledItems?: ScheduleItemsType;
    timezone?: string;
    statusFormat?: string;
  }
): CmsItemStatusValues {
  const displayStatus: Record<any, any> = {
    new: CMS_ITEM_STATUS.NEW,
    draft: isPublished(item, lastPublished)
      ? CMS_ITEM_STATUS.CHANGES_IN_DRAFT
      : CMS_ITEM_STATUS.DRAFT,
    published: CMS_ITEM_STATUS.PUBLISHED,
    staged: CMS_ITEM_STATUS.STAGED,
    stagedChanges: CMS_ITEM_STATUS.STAGED,
    archived: CMS_ITEM_STATUS.ARCHIVED,
    scheduled: getScheduledStatus({
      itemId: item.get('_id'),
      localeId: item.get('_locale'),
      scheduledItems,
      timezone,
      statusFormat,
    }),
    scheduledFailed: getFailedToPublishStatus({
      itemId: item.get('_id'),
      localeId: item.get('_locale'),
      scheduledItems,
      statusFormat,
    }),
  };
  return displayStatus[getStatus({item, scheduledItems, lastPublished})];
}

export const getStatusTextColorVariant = ({
  item,
  lastPublished,
  status,
  primaryColor = 'primary',
}: {
  item: Item;
  lastPublished: string | null;
  status: string;
  primaryColor?: 'primary' | 'blue';
}): TextProps['colorVariant'] => {
  switch (status) {
    case 'draft':
      return isPublished(item, lastPublished) ? 'orange' : 'primary';
    case 'published':
      return 'success';
    case 'scheduled':
    case 'stagedChanges':
    case 'staged':
      return primaryColor;
    case 'scheduledFailed':
      return 'danger';
    case 'archived':
      return 'secondary';
    default:
      return 'primary';
  }
};

export const getStatusButtonColorVariant = ({
  item,
  lastPublished,
  status,
  primaryColor = 'primary',
}: {
  item: Item;
  lastPublished: string | null;
  status: string;
  primaryColor?: 'primary' | 'blue';
}): ButtonProps['colorVariant'] => {
  switch (status) {
    case 'draft':
      return isPublished(item, lastPublished) ? 'orange' : 'default';
    case 'published':
      return 'green';
    case 'scheduled':
    case 'stagedChanges':
    case 'staged':
      return primaryColor;
    case 'scheduledFailed':
      return 'red';
    case 'archived':
      return 'secondary';
    default:
      return 'default';
  }
};

export function isArchived(item: Item) {
  return item.get('_archived', false);
}

export function isPublished(item: Item, lastPublished?: string | null) {
  return !isNil(lastPublished) && Boolean(item.get('published-on', false));
}

export function isPublishedWithChanges(item: Item) {
  const editTime = item.get('updated-on');
  const pubTime = item.get('published-on');

  return pubTime && editTime > pubTime;
}

export function isSuccessfullyScheduledItem({
  itemId,
  localeId,
  scheduledItems = [],
}: {
  itemId: string;
  localeId?: string;
  scheduledItems: ScheduleItemsType;
}) {
  return scheduledItems.some(
    (i) => matchesScheduledItem(i, itemId, localeId) && !i.publishError
  );
}

export function isFailedScheduledItem({
  itemId,
  localeId,
  scheduledItems = [],
}: {
  itemId: string;
  localeId?: string;
  scheduledItems: ScheduleItemsType;
}) {
  return scheduledItems.some(
    (i) => matchesScheduledItem(i, itemId, localeId) && i.publishError
  );
}

export const getScheduledTime = ({
  itemId,
  localeId,
  scheduledItems = [],
}: {
  itemId: string;
  localeId?: string;
  scheduledItems: ScheduleItemsType;
}) => {
  const scheduledItem = scheduledItems.find(
    (i) => matchesScheduledItem(i, itemId, localeId) && !i.publishError
  );
  return scheduledItem ? scheduledItem.scheduledTime : null;
};

const optionsById = (field: any) =>
  indexById(field.getIn(['validations', 'options'], List()));

/**
 * Checks if a field has a slug.
 *
 * Fields must have a slug to be used in leftJoin operations.
 */
const hasFieldSlug = (field: any): boolean => {
  const slug = field.get('slug');
  return Boolean(slug && typeof slug === 'string');
};

const expandOption = (transform: any) => (items: any, field: any) => {
  if (!hasFieldSlug(field)) {
    return items;
  }

  return leftJoin(transform, field.get('slug'), items, optionsById(field));
};

export const expandOptions = curry(
  <TItems extends Iterable<unknown, Item>>(
    transform: (item: unknown) => unknown,
    fields: List<Field>,
    items: TItems
  ): TItems => fields.filter(isOption).reduce(expandOption(transform), items)
);

export const expandSingleRefs = curry(
  <TItems extends Iterable<unknown, Item>>(
    transform: (item: unknown) => unknown,
    fields: List<Field>,
    allItems: Iterable<unknown, Item>,
    itemList: TItems
  ): TItems =>
    fields.filter(isSingleRef).reduce((items, field) => {
      if (!hasFieldSlug(field)) {
        return items;
      }
      return leftJoin(transform, field.get('slug'), items, allItems);
    }, itemList)
);

const getKeys = (
  keys: List<string>,
  struct: Map<string, unknown>,
  defaultValue = Map()
) => {
  return keys.map((k) => {
    const val = struct.get(k) || defaultValue;

    return val;
  });
};

export const expandItemRefSets = curry(
  <TItems extends Iterable<unknown, Item>>(
    transform: (item: unknown) => unknown,
    fields: List<Field>,
    allItems: Iterable<unknown, Item>,
    itemList: TItems
  ): TItems =>
    fields.filter(isItemRefSet).reduce((items, field) => {
      if (!hasFieldSlug(field)) {
        return items;
      }
      return leftJoinWith(
        getKeys,
        map(transform),
        field.get('slug'),
        items,
        allItems
      );
    }, itemList)
);

export const expandItemRefs = curry(
  <TItems extends Iterable<unknown, Item>>(
    transform: (item: unknown) => unknown,
    fields: List<Field>,
    allItems: Iterable<unknown, Item>,
    itemList: TItems
  ): TItems =>
    expandSingleRefs(
      transform,
      fields,
      allItems,
      expandItemRefSets(transform, fields, allItems, itemList)
    )
);

export const expandOptionObjects = expandOptions(identity);
const expandOptionObjectsForItem =
  (collections: Map<string, Collection>) => (item: Item) =>
    head(
      expandOptionObjects(
        collections.getIn([item.get('_cid'), 'fields']),
        List.of(item)
      )
    );

// collections : Map ID Collection -- A map of all collections
// fields : List Field -- The list of fields corresponding to the items in itemList
// allItems : Map ID Item -- A map of all items
// itemList : List Item -- A list of items to be expanded
export const expandAll = curry(
  <TItems extends Iterable<unknown, Item>>(
    collections: Map<string, Collection>,
    fields: List<Field>,
    allItems: Iterable<unknown, Item>,
    itemList: TItems
  ): TItems =>
    expandOptionObjects(
      fields,
      expandItemRefs(
        expandOptionObjectsForItem(collections),
        fields,
        allItems,
        itemList
      )
    )
);

export const getItemLabel = (
  item: Item,
  scheduledItems: ScheduleItemsType = [],
  canUseModal?: boolean
) => {
  const itemId = getId(item);
  const localeId = item.get('_locale');
  const label = item.get('name') as string;
  const isDraftItem = isDraft(item);
  const isArchivedItem = isArchived(item);
  const isDraftChangesItem = isPublishedWithChanges(item);

  if (isDraftItem && isArchivedItem) {
    return canUseModal ? label : '[Archived Draft] ' + label;
  } else if (isDraftChangesItem) {
    return canUseModal ? label : `[${CMS_CHANGES_IN_DRAFT_LABEL}] ${label}`;
  } else if (isDraftItem) {
    return canUseModal ? label : '[Draft] ' + label;
  } else if (isArchivedItem) {
    return canUseModal ? label : '[Archived] ' + label;
  } else if (isSuccessfullyScheduledItem({itemId, localeId, scheduledItems})) {
    return canUseModal ? label : '[Scheduled] ' + label;
  } else if (isFailedScheduledItem({itemId, localeId, scheduledItems})) {
    return '[Failed] ' + label;
  } else {
    return label;
  }
};

export const cleanItemOptionValues = (item: Item, fields: List<Field>) => {
  fields.filter(isOption).forEach((optionField) => {
    const options =
      // @ts-expect-error - TS2571 - Object is of type 'unknown'
      optionField.getIn(['validations', 'options']).map(getId);
    const slug = getSlug(optionField);
    const optionValue = item.get(slug);
    if (optionValue && !options.includes(optionValue)) {
      item = item.delete(slug);
    }
  });
  return item;
};

export const cleanMultiFieldValues = (item: Item, fields: List<Field>) => {
  return fields
    .filter((field) => field.type === 'Set')
    .reduce((cleanedItem, multiField) => {
      const fieldSlug = getSlug(multiField);
      const fieldValue = cleanedItem.get(fieldSlug);
      if (List.isList(fieldValue)) {
        const cleanFieldValue = fieldValue.filter(notNil);
        return cleanFieldValue.size > 0
          ? cleanedItem.set(fieldSlug, cleanFieldValue)
          : cleanedItem.set(fieldSlug, null);
      } else {
        return cleanedItem;
      }
    }, item);
};

// Plain javascript object version of cleanMultiFieldValues.
export const cleanMultiImageFieldValues = (
  fieldsToClean: Record<string, any>
) => {
  return Object.keys(fieldsToClean).reduce<Record<string, any>>(
    (acc, fieldSlug) => {
      const fieldValue = fieldsToClean[fieldSlug];
      if (List.isList(fieldValue)) {
        const cleanFieldValue = fieldValue.filter(notNil);
        acc[fieldSlug] = cleanFieldValue.size > 0 ? cleanFieldValue : null;
      } else {
        acc[fieldSlug] = fieldValue;
      }
      return acc;
    },
    {}
  );
};

// We cannot single-item publish an item that references directly or indirectly
// to an item that cannot be single-item published. The following references
// cannot be published.
// 1. A reference that is an unpublished item that is draft or archived
// 2. A reference whose publishing dependencies cause cyclic publish chain.
export const isPublishingBlockedByReference = ({
  collectionState,
  item,
  collectionId,
  locales,
}: {
  collectionState: CollectionState;
  item: Item;
  collectionId?: string;
  locales: Locale[];
}) => {
  if (!collectionId) {
    return true;
  }
  const {collections} = collectionState;
  const items = mapCmsItemsById(
    selectCmsItemsForLocales(collectionState, locales)
  );
  const unpublishedRefIds = getUnpublishedItemRefIds(
    item,
    collectionId,
    collections,
    items
  );

  const stack = unpublishedRefIds.map((refItemId) => {
    return {
      refItemId,
      refPath: [getId(item)],
    };
  });

  while (stack.length > 0) {
    const element = stack.shift();
    if (!element) {
      break;
    }
    const {refItemId, refPath} = element;
    const refItem = items.get(refItemId);
    const isCyclicReference = refPath.includes(refItemId);

    if (!refItem || isCyclicReference) {
      return true;
    }

    const refCollectionId = refItem.get('_cid');

    const unpublishedRefsOfRef = getUnpublishedItemRefIds(
      refItem,
      refCollectionId,
      collections,
      items
    );

    const toStack = unpublishedRefsOfRef.map((refOfRef) => {
      return {
        refItemId: refOfRef,
        refPath: refPath.concat(refItemId),
      };
    });

    stack.push(...toStack);
  }
  return false;
};

const cannotPublishReference = ({
  refItem,
  collections,
}: {
  refItem: Item;
  collections: CollectionsType;
}): boolean => {
  const refCollectionId = refItem.get('_cid');
  const refCollection = collections.get(refCollectionId);

  if (!refCollection) {
    return true;
  }

  return isArchived(refItem);
};

export const getReferencesToPublish = ({
  currentCollectionState,
  collectionState,
  locales,
}: {
  currentCollectionState: CurrentCollectionState;
  collectionState: CollectionState;
  locales: Locale[];
  lastPublished?: string;
}): List<{collection: Collection; item: Item}> => {
  const {currentItem, currentCollection, currentInlineItem} =
    currentCollectionState;
  let item = currentInlineItem ? currentInlineItem.get('item') : currentItem;

  if (!item) {
    return List<{collection: Collection; item: Item}>();
  }

  item = cleanItemOptionValues(item, currentCollection?.fields ?? List());
  const collection = currentInlineItem
    ? currentInlineItem.get('collection')
    : currentCollection;

  if (!collection) {
    return List<{collection: Collection; item: Item}>();
  }

  if (
    isPublishingBlockedByReference({
      collectionState,
      item,
      collectionId: getId(collection),
      locales,
    })
  ) {
    // If the publishing is blocked, there's no point to return any references.
    return List<{collection: Collection; item: Item}>();
  }

  return _getReferencesToPublish({
    item,
    collection,
    collectionState,
    locales,
  });
};

const _getReferencesToPublish = ({
  item,
  collection,
  collectionState,
  locales,
}: {
  item: Item;
  collection: Collection;
  collectionState: CollectionState;
  locales: Locale[];
  lastPublished?: string;
}) => {
  const {collections} = collectionState;
  const items = mapCmsItemsById(
    selectCmsItemsForLocales(collectionState, locales)
  );
  const unpublishedElements: Array<{
    collection: Collection;
    item: Item;
    fromInline?: boolean;
  }> = [];
  const collectionId = getId(collection);
  const unpublishedRefIds = getUnpublishedItemRefIds(
    item,
    collectionId,
    collections,
    items
  );

  const stack = unpublishedRefIds.map((refItemId) => {
    return {
      refItemId,
      refPath: [getId(item)],
    };
  });

  while (stack.length > 0) {
    const element = stack.shift();
    if (!element) {
      continue;
    }
    const {refItemId, refPath} = element;
    const refItem = items.get(refItemId);
    const isCyclicReference = refPath.includes(refItemId);

    if (
      !refItem ||
      isCyclicReference ||
      cannotPublishReference({
        refItem,
        collections,
      })
    ) {
      continue;
    }

    const refCollectionId = refItem.get('_cid');

    const unpublishedRefsOfRef = getUnpublishedItemRefIds(
      refItem,
      refCollectionId,
      collections,
      items
    );

    const toStack = unpublishedRefsOfRef.map((refOfRef) => {
      return {
        refItemId: refOfRef,
        refPath: refPath.concat(refItemId),
      };
    });

    stack.push(...toStack);

    if (
      unpublishedElements.every((itemCol) => {
        const itemColId = itemCol.item.get('_id');
        return itemColId !== refItemId;
      })
    ) {
      unpublishedElements.push({
        item: refItem.set('_draft', false),
        collection: collections.get(refCollectionId),
        fromInline: true,
      });
    }
  }

  return List(unpublishedElements);
};

export const getNumberOfFailedToPublishItems = (
  scheduledItems: ScheduleItemsType = []
) => scheduledItems.filter((item) => item.publishError).length;

export const getNumberOfFailedToPublishItemsByCollection = (
  scheduledItems: ScheduleItemsType = [],
  collectionId?: string
) =>
  scheduledItems.filter(
    (item) => item.publishError && item.collectionId === collectionId
  ).length;

export const groupScheduledItems = ({
  successfullyScheduledItems = [],
  collectionState,
}: {
  successfullyScheduledItems?: ScheduleItemsType;
  collectionState: CollectionState;
}) => {
  const items = selectCmsItemsIgnoringLocalization(collectionState);
  return successfullyScheduledItems.reduce((groupedItems, scheduleItem) => {
    const item = items.get(scheduleItem.itemId);
    const collection = collectionState.collections.get(
      scheduleItem.collectionId
    );
    return item && collection
      ? groupedItems.push({item, collection})
      : groupedItems;
  }, List());
};

// NOTE: we need to have `collectionId` in the function signature instead of
// reading item.get('_cid') because mint items don't have `item._cid`
const getUnpublishedItemRefIds = (
  item: Item,
  collectionId: string,
  collections: CollectionsType,
  items: ItemsType
) => {
  const fields: Field[] = collections.getIn([collectionId, 'fields']);
  const refFields = fields && fields.filter(isItemRef);
  const refFieldSlugs =
    refFields && refFields.map((field) => field.get('slug'));
  return refFieldSlugs
    ? refFieldSlugs.reduce<Array<string>>((memo, refFieldSlug) => {
        const refFieldValue: List<string> | string = item.get(refFieldSlug);
        if (List.isList(refFieldValue)) {
          memo.push(
            ...refFieldValue
              .filter(
                (refId) =>
                  refId !== undefined &&
                  (isUnpublishedItemId(refId, items) ||
                    isDraftChangesItemId(refId, items))
              )
              .toArray()
          );
        } else if (
          typeof refFieldValue === 'string' &&
          (isUnpublishedItemId(refFieldValue, items) ||
            isDraftChangesItemId(refFieldValue, items))
        ) {
          memo.push(refFieldValue);
        }
        return memo;
      }, [])
    : [];
};

const isUnpublishedItemId = (itemId: string, items: ItemsType) => {
  const item = items.get(itemId);
  return Boolean(item && isNil(item.get('published-on')));
};

const isDraftChangesItemId = (itemId: string, items: ItemsType) => {
  const item = items.get(itemId);
  return Boolean(
    item &&
    Boolean(item.get('_draft', false)) &&
    Boolean(item.get('published-on', false))
  );
};

export const getItemsForCollection = <
  TItems extends Iterable<ItemId | number, Item>,
>(
  items: TItems,
  collectionId: string
): TItems => {
  return memoizedGetItemsForCollection(items)(collectionId);
};

// Max number of reference fields is 10
const collectionIdMemo = cacheMemo(10);

const memoizedGetItemsForCollection = weakMemo<
  <TItems extends Iterable<ItemId | number, Item>>(
    items: TItems
  ) => (collectionId: string) => TItems
>((items) =>
  collectionIdMemo((collectionId: string) => {
    return items.filter((item) => item.get('_cid') === collectionId);
  })
);
